import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Icon,
  InputLabel,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Check, Close, FileUploadOutlined, Send } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import InvoicePreview from "./components/InvoicePreview";
import {
  acknowledgePO,
  getCreditNoteAttachments,
  getCreditNoteDetails,
  updateCreditNote,
} from "../../services/invoiceService";
import { getPODetails } from "./v2/api/invoiceQueries";
import { useWalletFlag } from "../Payments/hooks/useWalletFlag";
import {
  CREDIT_NOTE_FINANCE_APPROVAL_STEP_NAMES,
  getCreditNoteWorkflowRequestForPO,
  uploadAttachment,
} from "../../services/poService";
import { usePermissions } from "../../context/PermissionsContext";
import { BOUNCER_PERMISSIONS } from "../../constants/bouncerPermissions";
import { convertToINR } from "../../helpers/Utils";
import { getBackendErrorMessage } from "../../helpers/errorUtils";
import { TOKENS } from "./constants/figmaTokens";
import { useToast } from "./hooks/useToast";
import { useInvoiceV2Flag } from "./hooks/useInvoiceV2Flag";
import { useIsMobile } from "../../hooks/useIsMobile";
import StickyActionBar from "../../components/mobile/StickyActionBar";
import {
  buildResendCreditNotePayload,
  canApproveCreditNote,
  resendRefundUtrRule,
  shouldShowVendorCreditNoteActions,
} from "./utils/creditNote";
import { invoiceKeys } from "./v2/api/queryKeys";

export default function CreditNoteReview({ creditNote, handleClose, po_code, po_id, disableRedirect = false }) {
  const { hasPermission } = usePermissions();
  const canApprove = hasPermission(BOUNCER_PERMISSIONS.CREDIT_NOTE_APPROVAL);
  const canUpload = hasPermission(BOUNCER_PERMISSIONS.INVOICE_UPLOAD);
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobileViewport = useIsMobile();
  const { isV2 } = useInvoiceV2Flag();
  const isMobile = isV2 && isMobileViewport;

  const [isClicked, setIsClicked] = useState("");
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [hasReUploaded, setHasReUploaded] = useState(false);
  const [showReUploadDialog, setShowReUploadDialog] = useState(false);
  const [stagedFile, setStagedFile] = useState(null);
  const [stagedPreviewUrl, setStagedPreviewUrl] = useState(null);
  const [stagedCnNumber, setStagedCnNumber] = useState("");
  const [stagedCnValue, setStagedCnValue] = useState("");
  const [stagedRefundUtr, setStagedRefundUtr] = useState("");
  const stagedSeededRef = useRef(false);

  const { data: creditNoteDetails } = useQuery(
    ["creditNoteDetails", creditNote?.id],
    () => getCreditNoteDetails(creditNote.id),
    {
      enabled: !!creditNote?.id,
      retry: false,
    }
  );

  const { data: attachments, isLoading, isError } = useQuery(
    ["creditNoteAttachments", creditNote?.id],
    () => getCreditNoteAttachments(creditNote.id),
    {
      enabled: !!creditNote?.id,
      retry: false,
    }
  );

  // v2 attachments API returns { attachments: [{ id, name, url, ... }] }
  const attachment = attachments?.attachments?.[0];
  const fileUrl = attachment?.url;
  const fileName = attachment?.name;

  const handleApprove = async () => {
    setIsClicked("approve");
    try {
      await updateCreditNote(creditNote.id, {
        action: "approve",
        request_id: financeRequestId,
        step_id: financeStepId,
      });
      showToast({ type: "success", message: "Credit note approved successfully" });
      queryClient.invalidateQueries({ queryKey: ["invoiceListQuery"] });
      queryClient.invalidateQueries({ queryKey: ["invoiceList"] });
      queryClient.invalidateQueries(invoiceKeys.lists());
      queryClient.invalidateQueries(invoiceKeys.summary());
      queryClient.invalidateQueries({ queryKey: ["creditNoteDetails", creditNote.id] });
      queryClient.invalidateQueries({ queryKey: ["creditNoteAttachments", creditNote.id] });
      handleClose();
      if (!disableRedirect) navigate("/dashboard/invoice-management");
    } catch (err) {
      showToast({
        type: "error",
        message: `Failed to approve credit note: ${getBackendErrorMessage(err, "unknown error")}`,
      });
    } finally {
      setIsClicked("");
    }
  };

  const handleReject = async () => {
    setIsClicked("reject");
    try {
      await updateCreditNote(creditNote.id, {
        action: "reject",
        request_id: financeRequestId,
        step_id: financeStepId,
        reason: rejectionReason.trim(),
      });
      showToast({ type: "success", message: "Credit note rejected successfully" });
      queryClient.invalidateQueries({ queryKey: ["invoiceListQuery"] });
      queryClient.invalidateQueries({ queryKey: ["invoiceList"] });
      queryClient.invalidateQueries(invoiceKeys.lists());
      queryClient.invalidateQueries(invoiceKeys.summary());
      queryClient.invalidateQueries({ queryKey: ["creditNoteDetails", creditNote.id] });
      queryClient.invalidateQueries({ queryKey: ["creditNoteAttachments", creditNote.id] });
      setShowRejectionModal(false);
      handleClose();
      if (!disableRedirect) navigate("/dashboard/invoice-management");
    } catch (err) {
      showToast({
        type: "error",
        message: `Failed to reject credit note: ${getBackendErrorMessage(err, "unknown error")}`,
      });
    } finally {
      setIsClicked("");
    }
  };

  const childModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? "92vw" : 560,
    bgcolor: "background.paper",
    borderRadius: "4px",
  };

  const cnAmount =
    creditNoteDetails?.credit_note_value ??
    creditNote?.credit_note_value ??
    creditNote?.amount;
  const cnStatus = creditNote?.status ?? creditNoteDetails?.status;
  const cnRejectionReason =
    creditNote?.reason || creditNoteDetails?.metadata?.action_reason || "";
  const showActions = canApproveCreditNote({ canApprove, cnStatus });
  const showVendorActions = shouldShowVendorCreditNoteActions({
    status: cnStatus,
    canUpload,
  });
  const existingUtrNumber =
    creditNoteDetails?.utr_number ?? creditNote?.utr_number ?? "";
  // The re-upload offers the refund UTR on the same rule as the first submission,
  // not only when the original had one (ENG-28637). Paid amount: PO details'
  // `total_amount_paid`, the figure VIP's own refund check uses.
  const walletOn = useWalletFlag();
  const poDetailsQuery = useQuery({
    queryKey: invoiceKeys.poDetails(po_code),
    queryFn: () => getPODetails(po_code),
    enabled: !!po_code && showVendorActions,
    retry: false,
    staleTime: 60_000,
  });
  const refundUtrRule = resendRefundUtrRule({
    existingUtr: existingUtrNumber,
    totalPaid: poDetailsQuery.data?.po_list?.[0]?.total_amount_paid,
    revisedPoValue: creditNoteDetails?.metadata?.revised_po_value,
    walletOn,
  });
  const cnInvoiceIds =
    creditNoteDetails?.invoice_ids ?? creditNote?.invoice_ids ?? [];
  const existingAttachmentId =
    creditNoteDetails?.attachment_id ?? creditNote?.attachment_id ?? null;

  useEffect(() => {
    if (!stagedSeededRef.current && creditNoteDetails) {
      setStagedCnNumber(
        creditNoteDetails.credit_note_number ?? creditNote?.number ?? ""
      );
      setStagedCnValue(
        creditNoteDetails.credit_note_value != null
          ? String(creditNoteDetails.credit_note_value)
          : ""
      );
      setStagedRefundUtr(existingUtrNumber);
      stagedSeededRef.current = true;
    }
  }, [creditNoteDetails, creditNote, existingUtrNumber]);

  useEffect(() => {
    if (!stagedFile) {
      setStagedPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(stagedFile);
    setStagedPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [stagedFile]);

  const financeWorkflowQuery = useQuery({
    queryKey: ["cn-finance-workflow-requests", po_code],
    queryFn: () => getCreditNoteWorkflowRequestForPO(po_code),
    enabled: !!po_code && showActions,
    retry: false,
  });
  const firstFinanceWorkflowItem = financeWorkflowQuery.data?.items?.[0];
  const financeRequestId = firstFinanceWorkflowItem?.id;
  const financeStepId = firstFinanceWorkflowItem?.all_steps?.find(
    (s) =>
      s?.status === "PENDING" &&
      CREDIT_NOTE_FINANCE_APPROVAL_STEP_NAMES.includes(s?.name)
  )?.id;
  const canFinanceAct = !!financeRequestId && !!financeStepId;

  const resendMutation = useMutation({
    mutationFn: async () => {
      let attachmentId = existingAttachmentId;
      if (stagedFile) {
        const att = await uploadAttachment(stagedFile);
        attachmentId = att?.id ?? null;
      }
      const data = buildResendCreditNotePayload({
        po_code,
        po_id,
        invoiceIds: cnInvoiceIds,
        cnNumber: stagedCnNumber,
        cnValue: stagedCnValue,
        attachmentId,
        refundUtr: stagedRefundUtr,
        showRefundUtr: refundUtrRule.show,
      });
      return acknowledgePO({ data });
    },
    onSuccess: () => {
      showToast({ type: "success", message: "Credit note sent for L2 approval" });
      queryClient.invalidateQueries({ queryKey: ["invoiceListQuery"] });
      queryClient.invalidateQueries({ queryKey: ["invoiceList"] });
      queryClient.invalidateQueries(invoiceKeys.lists());
      queryClient.invalidateQueries(invoiceKeys.summary());
      queryClient.invalidateQueries({ queryKey: ["creditNoteDetails", creditNote?.id] });
      handleClose();
      if (!disableRedirect) navigate("/dashboard/invoice-management");
    },
    onError: (err) => {
      showToast({
        type: "error",
        message: `Resend failed: ${getBackendErrorMessage(err, "unknown error")}`,
      });
    },
  });

  const canResend =
    hasReUploaded &&
    !!stagedCnNumber &&
    !!stagedCnValue &&
    (!refundUtrRule.required || !!stagedRefundUtr.trim()) &&
    // Resend needs the PO's paid amount: without it the UTR field cannot be offered,
    // so a resend would park the excess unasked (review W1). `isSuccess`, not
    // `!isLoading`: v4 reports a disabled query as loading, and a failed refetch as
    // an error even with cached data — either way, no confirmed paid amount.
    poDetailsQuery.isSuccess &&
    !resendMutation.isLoading;

  const handleOpenReUpload = () => setShowReUploadDialog(true);
  const handleSubmitReUpload = (next) => {
    setStagedFile(next.file ?? stagedFile);
    setStagedCnNumber(next.cnNumber);
    setStagedCnValue(next.cnValue);
    setStagedRefundUtr(next.refundUtr);
    setHasReUploaded(true);
    setShowReUploadDialog(false);
  };
  const handleCancelReUpload = () => setShowReUploadDialog(false);
  const handleResend = () => resendMutation.mutate();
  const handleVendorCancel = () => {
    setStagedFile(null);
    setStagedCnNumber("");
    setStagedCnValue("");
    setStagedRefundUtr("");
    setHasReUploaded(false);
    stagedSeededRef.current = false;
    handleClose();
  };
  const onCloseDispatch = showVendorActions ? handleVendorCancel : handleClose;

  const approverActions = (
    <>
      <Button
        variant="contained"
        startIcon={<Close />}
        onClick={() => {
          setRejectionReason("");
          setShowRejectionModal(true);
        }}
        disabled={!!isClicked || !canFinanceAct}
        sx={{
          bgcolor: TOKENS.surfaceVariant,
          color: TOKENS.onSurfaceSecondary,
          boxShadow: "none",
          height: 36,
          textTransform: "none",
          "&:hover": { bgcolor: TOKENS.surfaceVariant },
        }}
      >
        Reject
      </Button>
      <Button
        variant="contained"
        startIcon={<Check />}
        onClick={handleApprove}
        disabled={!!isClicked || !canFinanceAct}
        endIcon={
          isClicked === "approve" ? (
            <CircularProgress size={14} thickness={6} sx={{ color: "#fff" }} />
          ) : null
        }
        sx={{
          bgcolor: TOKENS.primaryDefault,
          color: "#FFFFFF",
          boxShadow: "none",
          height: 36,
          textTransform: "none",
          "&:hover": { bgcolor: TOKENS.primaryDefault },
        }}
      >
        Accept
      </Button>
    </>
  );

  const vendorActions = (
    <>
      <Button
        variant="contained"
        startIcon={<Close />}
        onClick={handleVendorCancel}
        disabled={resendMutation.isLoading}
        sx={{
          bgcolor: TOKENS.surfaceVariant,
          color: TOKENS.onSurfaceSecondary,
          boxShadow: "none",
          height: 36,
          textTransform: "none",
          "&:hover": { bgcolor: TOKENS.surfaceVariant },
        }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        startIcon={<FileUploadOutlined />}
        onClick={handleOpenReUpload}
        disabled={resendMutation.isLoading}
        sx={{
          bgcolor: TOKENS.surfaceVariant,
          color: TOKENS.onSurfaceSecondary,
          boxShadow: "none",
          height: 36,
          textTransform: "none",
          "&:hover": { bgcolor: TOKENS.surfaceVariant },
        }}
      >
        Re-Upload
      </Button>
      <Button
        variant="contained"
        startIcon={<Send />}
        onClick={handleResend}
        disabled={!canResend}
        endIcon={
          resendMutation.isLoading ? (
            <CircularProgress size={14} thickness={6} sx={{ color: "#fff" }} />
          ) : null
        }
        sx={{
          bgcolor: TOKENS.primaryDefault,
          color: "#FFFFFF",
          boxShadow: "none",
          height: 36,
          textTransform: "none",
          opacity: canResend ? 1 : 0.5,
          "&:hover": { bgcolor: TOKENS.primaryDefault },
          "&.Mui-disabled": {
            bgcolor: TOKENS.primaryDefault,
            color: "#FFFFFF",
            opacity: 0.5,
          },
        }}
      >
        Resend for L2 approval
      </Button>
    </>
  );

  return (
    <Modal open={!!creditNote} onClose={onCloseDispatch}>
      <Box height={"100vh"} display={"flex"} flexDirection={"column"}>
        <Box
          sx={{
            minHeight: 60,
            padding: isMobile ? "8px 12px 8px 8px" : "8px 24px 8px 8px",
            bgcolor: TOKENS.surfaceDefault,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "16px",
            borderBottom: `1px solid ${TOKENS.surfaceVariant}`,
          }}
        >
          <Box
            onClick={onCloseDispatch}
            sx={{
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              cursor: "pointer",
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
            }}
          >
            <ArrowBack sx={{ fontSize: 24, color: TOKENS.onSurfaceSecondary }} />
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-start" pb="4px">
            <Typography
              sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}
            >
              PO Order id/
            </Typography>
            <Box display="flex" alignItems="center" gap="12px">
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 600,
                  lineHeight: "24px",
                  color: TOKENS.onSurfaceDefault,
                }}
              >
                #{creditNote?.number || "Credit Note"}
              </Typography>
              {cnStatus && (
                <Box
                  sx={{
                    bgcolor:
                      cnStatus === "APPROVED"
                        ? TOKENS.tertiaryBlueVariant2
                        : cnStatus === "REJECTED"
                        ? TOKENS.tertiaryRedVariant2
                        : TOKENS.tertiaryYellowVariant2,
                    color:
                      cnStatus === "APPROVED"
                        ? TOKENS.onTertiaryBlueVariant
                        : cnStatus === "REJECTED"
                        ? TOKENS.onTertiaryRedVariant
                        : TOKENS.onTertiaryYellowVariant,
                    px: "8px",
                    py: "2px",
                    borderRadius: "4px",
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: "16px",
                  }}
                >
                  {cnStatus}
                </Box>
              )}
            </Box>
            {showVendorActions && cnRejectionReason && (
              <Typography
                sx={{
                  fontSize: 12,
                  lineHeight: "16px",
                  color: TOKENS.tertiaryRedDefault,
                  marginTop: "4px",
                }}
              >
                Reason: {cnRejectionReason}
              </Typography>
            )}
            {isMobile && cnAmount != null && (
              <Typography
                sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}
              >
                Credit note Amount · {convertToINR(cnAmount)}
              </Typography>
            )}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {!isMobile && cnAmount != null && (
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              <Typography
                sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}
              >
                Credit note Amount
              </Typography>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 600,
                  lineHeight: "24px",
                  color: TOKENS.onSurfaceDefault,
                }}
              >
                {convertToINR(cnAmount)}
              </Typography>
            </Box>
          )}

          {!isMobile && showActions && (
            <Stack direction="row" spacing={1}>
              {approverActions}
            </Stack>
          )}

          {!isMobile && showVendorActions && (
            <Stack direction="row" spacing={1}>
              {vendorActions}
            </Stack>
          )}
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            bgcolor: "#F2F2F2",
            overflowY: "scroll",
            display: "flex",
            justifyContent: "center",
            padding: isMobile ? "12px" : "24px",
          }}
        >
          <Box maxWidth={900} width="100%">
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" padding={16}>
                <CircularProgress />
              </Box>
            ) : stagedFile && stagedPreviewUrl ? (
              <InvoicePreview
                type={stagedFile.name.split(".").pop()?.toLowerCase()}
                url={stagedPreviewUrl}
                scale={isMobile ? 1 : 1.5}
                maxHeight="none"
              />
            ) : isError || !fileUrl ? (
              <Card
                sx={{
                  padding: "40px",
                  textAlign: "center",
                  color: "#999",
                }}
              >
                <Typography variant="body1">
                  No attachment available
                </Typography>
              </Card>
            ) : (
              <InvoicePreview
                type={fileName?.split(".").pop()?.toLowerCase()}
                url={fileUrl}
                scale={isMobile ? 1 : 1.5}
                maxHeight="none"
              />
            )}
          </Box>
        </Box>
        {isMobile && showActions && <StickyActionBar>{approverActions}</StickyActionBar>}
        {isMobile && showVendorActions && <StickyActionBar>{vendorActions}</StickyActionBar>}
        <Modal
          open={showRejectionModal}
          onClose={() => setShowRejectionModal(false)}
        >
          <Box sx={childModalStyle}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              borderBottom={"1px solid #E6E6E6"}
              padding={"16px"}
            >
              <Typography variant="h4">Credit Note Rejected</Typography>
              <Icon
                sx={{ cursor: "pointer" }}
                onClick={() => setShowRejectionModal(false)}
              >
                <Close fontSize="24px" />
              </Icon>
            </Box>
            <Box padding={"24px"}>
              <InputLabel htmlFor="rejectionReasons">
                Enter rejection reason
              </InputLabel>
              <TextField
                id="rejectionReasons"
                InputLabelProps={{ shrink: false }}
                sx={{ marginTop: "12px" }}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                fullWidth
              />
            </Box>
            <Box
              display={"flex"}
              justifyContent={"flex-end"}
              alignItems={"center"}
              columnGap={"12px"}
              sx={{ padding: "24px" }}
            >
              <Button
                color="secondary"
                variant="contained"
                onClick={() => setShowRejectionModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleReject}
                disabled={rejectionReason.trim() === "" || isClicked === "reject"}
                endIcon={
                  isClicked === "reject" ? (
                    <CircularProgress size={14} thickness={6} />
                  ) : (
                    ""
                  )
                }
              >
                Reject
              </Button>
            </Box>
          </Box>
        </Modal>
        <ReUploadDialog
          open={showReUploadDialog}
          refundUtrRule={refundUtrRule}
          initialValues={{
            file: stagedFile,
            cnNumber: stagedCnNumber,
            cnValue: stagedCnValue,
            refundUtr: stagedRefundUtr,
          }}
          onCancel={handleCancelReUpload}
          onSubmit={handleSubmitReUpload}
          isMobile={isMobile}
        />
      </Box>
    </Modal>
  );
}

function ReUploadDialog({ open, initialValues, refundUtrRule, onCancel, onSubmit, isMobile = false }) {
  const [file, setFile] = useState(initialValues.file);
  const [cnNumber, setCnNumber] = useState(initialValues.cnNumber);
  const [cnValue, setCnValue] = useState(initialValues.cnValue);
  const [refundUtr, setRefundUtr] = useState(initialValues.refundUtr);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFile(initialValues.file);
      setCnNumber(initialValues.cnNumber);
      setCnValue(initialValues.cnValue);
      setRefundUtr(initialValues.refundUtr);
    }
  }, [open, initialValues.file, initialValues.cnNumber, initialValues.cnValue, initialValues.refundUtr]);

  const handleFile = (f) => {
    if (f) setFile(f);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const refundUtrMissing = refundUtrRule.required && !refundUtr.trim();
  const canSave = !!cnNumber && !!cnValue && !refundUtrMissing;
  // Same hint as the first submission; without both figures it names "the excess".
  const refundUtrHint = refundUtrRule.required
    ? refundUtrMissing
      ? "Required"
      : ""
    : `Enter the UTR if you have transferred ${
        refundUtrRule.overpaid > 0 ? convertToINR(refundUtrRule.overpaid) : "the excess"
      } back. Leave blank to hold it as credit against your next payment.`;

  const handleSave = () => {
    onSubmit({ file, cnNumber, cnValue, refundUtr });
  };

  return (
    <Modal open={open} onClose={onCancel}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "92vw" : 560,
          bgcolor: "background.paper",
          borderRadius: "4px",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          borderBottom={"1px solid #E6E6E6"}
          padding={"16px"}
        >
          <Typography variant="h4">Re-Upload Credit Note</Typography>
          <Icon sx={{ cursor: "pointer" }} onClick={onCancel}>
            <Close fontSize="24px" />
          </Icon>
        </Box>

        <Box padding={isMobile ? "16px" : "24px"} display={"flex"} flexDirection={"column"} gap={"20px"}>
          <Box
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              bgcolor: dragOver ? "rgba(54, 85, 242, 0.04)" : TOKENS.surfaceDefault,
              border: `1px ${dragOver ? "dashed" : "solid"} ${
                dragOver ? TOKENS.primaryDefault : TOKENS.onSurfaceBorder
              }`,
              borderRadius: "8px",
              minHeight: 120,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "16px",
              cursor: "pointer",
            }}
          >
            <FileUploadOutlined sx={{ fontSize: 28, color: TOKENS.primaryDefault }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: TOKENS.onSurfaceDefault }}>
              {file ? file.name : "Drop file here or click to upload"}
            </Typography>
            {file && (
              <Typography sx={{ fontSize: 12, color: TOKENS.onSurfaceSecondary }}>
                Click to choose a different file
              </Typography>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
              style={{ display: "none" }}
            />
          </Box>

          <Divider />

          <Box>
            <InputLabel sx={{ marginBottom: "4px" }}>Credit note number</InputLabel>
            <TextField
              fullWidth
              size="small"
              value={cnNumber}
              onChange={(e) => setCnNumber(e.target.value)}
              error={!cnNumber}
              helperText={!cnNumber ? "Required" : ""}
            />
          </Box>

          <Box>
            <InputLabel sx={{ marginBottom: "4px" }}>Credit note value</InputLabel>
            <TextField
              fullWidth
              size="small"
              value={cnValue}
              onChange={(e) => setCnValue(e.target.value)}
              error={!cnValue}
              helperText={!cnValue ? "Required" : ""}
            />
          </Box>

          {refundUtrRule.show && (
            <Box>
              <InputLabel sx={{ marginBottom: "4px" }}>
                Refund UTR Number{refundUtrRule.required ? "" : " (optional)"}
              </InputLabel>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. ICIC0123456789"
                value={refundUtr}
                onChange={(e) => setRefundUtr(e.target.value)}
                error={refundUtrMissing}
                helperText={refundUtrHint}
              />
            </Box>
          )}
        </Box>

        <Box
          display={"flex"}
          justifyContent={"flex-end"}
          alignItems={"center"}
          columnGap={"12px"}
          sx={{ padding: isMobile ? "16px" : "24px", borderTop: "1px solid #E6E6E6" }}
        >
          <Button color="secondary" variant="contained" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
